import { useState, type ReactNode } from 'react';
import { Popover } from './Popover';
import { StaticValue, useEditing } from './Field';

export interface SelectOption {
  value: string;
  label: string;
  hint?: string;
}

/**
 * A listbox built from buttons rather than a native select, so it renders the
 * same on every platform and can carry a hint column.
 */
export function Select({
  value,
  options,
  onChange,
  placeholder = 'Select',
  align = 'left',
  footer,
}: {
  value: string | null;
  options: SelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  align?: 'left' | 'right';
  /**
   * An action below the list, separated from it. The territory picker uses it
   * to offer that territory's settings: switching territory and configuring
   * one are the same thought, and asking for a second control elsewhere on the
   * page to complete it is a step nobody remembers.
   */
  footer?: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const editing = useEditing();
  const current = options.find((o) => o.value === value);

  if (!editing) return <StaticValue>{current?.label ?? ''}</StaticValue>;

  return (
    <div className="pop-anchor">
      <button
        type="button"
        className="btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {current?.label ?? placeholder}
        <Chevron />
      </button>

      <Popover open={open} onClose={() => setOpen(false)} align={align}>
        <div role="listbox">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={option.value === value}
              className="pop-item"
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
            >
              <span>{option.label}</span>
              {option.hint ? <span className="mono">{option.hint}</span> : null}
            </button>
          ))}
        </div>
        {footer ? <div className="pop-foot">{footer(() => setOpen(false))}</div> : null}
      </Popover>
    </div>
  );
}

export function Chevron() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
      <path
        d="M2 4l3 3 3-3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
